import merge from 'lodash.merge';
import { grey } from '@mui/material/colors';
import { createTheme, PaletteMode, Theme, ThemeOptions } from '@mui/material/styles';
import getAppSurfaces from './appSurfaces';
import baseTheme from './base.theme';

/**
 * Colors an organization can override through its custom theme (stored as JSON
 * in `organizationSettings`). Any missing key falls back to the Evodicom default.
 */
export interface CustomThemeData {
  primary?: string;
  primaryDark?: string;
  primaryText?: string;
  primaryContrast?: string;
  secondary?: string;
  secondaryContrast?: string;
  info?: string;
  positive?: string;
  error?: string;
  errorDark?: string;
  errorContrast?: string;
  warning?: string;
  brandPrimary?: string;
  brandSecondary?: string;
  shades?: Partial<Record<50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900, string>>;
}

/**
 * - `app`: every page of the app, including the dialogs/drawers opened from the
 *   study page. Adds the shared surface layer (cards, dialogs, drawers,
 *   popovers, tables, inputs) on top of the base theme.
 * - `viewer`: base palette only, without the surface layer; used by
 *   `viewer.theme.ts`. The OHIF viewer itself is styled by its own CSS.
 */
export type ThemeScope = 'app' | 'viewer';

const defaultShades: Record<PaletteMode, Record<number, string>> = {
  light: {
    50: '#ffffff',
    100: '#e3e3e3',
    200: '#bfbfbf',
    300: '#adadad',
    400: '#9c9c9c',
    500: '#7d7d7d',
    600: '#636363',
    700: '#474747',
    800: '#262626',
    900: '#000000',
  },
  dark: {
    50: '#01070C',
    100: '#060C11',
    200: '#091016',
    300: '#0C1822',
    400: '#162436',
    500: '#1c364d',
    600: '#2e4a63',
    700: '#415263',
    800: '#5b6875',
    900: '#ffffff',
  },
};

const iconWithSpacing = { '.iconWithSpacing': { marginRight: '5px' } };

const getLightOptions = (data: CustomThemeData | null): ThemeOptions => {
  const isCustom = !!data;
  const primary = data?.primary || '#0E70FF';
  const primaryDark = data?.primaryDark || '#1A4488';
  const primaryText = data?.primaryText || 'rgba(0, 14, 37, 0.85)';
  const primaryContrast = data?.primaryContrast || '#ffffff';
  const secondary = data?.secondary || '#1A4488';
  const secondaryContrast = data?.secondaryContrast || '#f7f7f7';
  const info = data?.info || '#5FA2FF';
  const positive = data?.positive || '#149511';
  const error = data?.error || '#A70D0D';
  const errorDark = data?.errorDark || '#870505';
  const errorContrast = data?.errorContrast || '#f7f7f7';
  const warning = data?.warning || '#DC7414';
  const brandPrimary = data?.brandPrimary || '#0E70FF';
  const brandSecondary = data?.brandSecondary || '#1A4488';
  const shades = { ...defaultShades.light, ...data?.shades };

  const inputStyles = {
    height: '36px',
    paddingTop: 0,
    paddingBottom: 0,
    backgroundColor: shades[50],
    border: `1px solid ${shades[300]}`,
  };

  return {
    palette: {
      mode: 'light',
      primary: { main: primary, dark: primaryDark, contrastText: primaryContrast },
      secondary: { main: secondary, contrastText: secondaryContrast },
      text: { primary: primaryText },
      success: { main: positive },
      error: { main: error, contrastText: errorContrast },
      info: { main: info },
      warning: { main: warning },
      shade: { ...shades },
      background: { default: shades[50] },
      action: { hover: shades[200] },
      brand: { main: brandPrimary, dark: brandSecondary, contrastText: primaryContrast },
    },
    components: {
      MuiAppBar: { styleOverrides: { root: { background: shades[50] } } },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: shades[200],
            borderRight: 'none',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.3)',
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            ...inputStyles,
            '&.Mui-focused .MuiSelectInput-notchedOutline, &:hover .MuiSelectInput-notchedOutline':
              { border: `1px solid ${shades[500]}` },
          },
        },
      },
      MuiInput: {
        styleOverrides: {
          root: {
            ...inputStyles,
            '&.Mui-focused .MuiInput-notchedOutline, &:hover .MuiInput-notchedOutline': {
              border: `1px solid ${shades[500]}`,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            ...inputStyles,
            '&.Mui-focused .MuiOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-notchedOutline':
              { border: `1px solid ${shades[300]}` },
          },
        },
      },
      MuiTooltip: { styleOverrides: { tooltip: { backgroundColor: 'rgba(0, 46, 122, 0.9)' } } },
      MuiTableCell: {
        styleOverrides: {
          root: {},
          head: {
            backgroundColor: shades[200],
            color: isCustom ? shades[700] : grey[600],
            fontWeight: 500,
            textTransform: 'uppercase',
          },
          body: {
            backgroundColor: shades[50],
            borderBottom: '1px solid rgba(224, 224, 224, 0.2)',
          },
        },
      },
      MuiButton: {
        variants: [
          {
            props: { variant: 'contained', color: 'primary' },
            style: {
              ...(isCustom
                ? {
                    background: `linear-gradient(165deg, ${primary}, ${primaryDark})`,
                    '&:hover': { background: `linear-gradient(300deg, ${primary}, ${primaryDark})` },
                  }
                : {
                    background: `linear-gradient(180deg, ${brandPrimary}, ${brandSecondary})`,
                    '&:hover': {
                      background: `linear-gradient(0deg, ${brandPrimary}, ${brandSecondary})`,
                      opacity: 0.9,
                    },
                  }),
              color: 'white',
              ...iconWithSpacing,
              '&.Mui-disabled': { background: shades[300] },
            },
          },
          {
            props: { variant: 'contained', color: 'secondary' },
            style: {
              background: grey[200],
              color: '#0055dd',
              '&:hover': { background: grey[300] },
              ...iconWithSpacing,
              '&.Mui-disabled': { background: grey[200], color: grey[500] },
            },
          },
          {
            props: { variant: 'contained', color: 'error' },
            style: {
              background: `linear-gradient(180deg, ${error}, ${errorDark})`,
              color: 'white',
              '&:hover': { background: `linear-gradient(0deg, ${error}, ${errorDark})` },
              ...iconWithSpacing,
              '&.Mui-disabled': { background: grey[200], color: grey[500] },
            },
          },
          {
            props: { variant: 'outlined', color: 'secondary' },
            style: {
              border: `1px solid ${isCustom ? secondary : shades[900]}`,
              color: isCustom ? secondary : shades[900],
              '&:hover': { border: `1px solid ${info}`, color: info },
              ...iconWithSpacing,
              '&.Mui-disabled': { color: grey[500] },
            },
          },
        ],
      },
      MuiPaper: { styleOverrides: { root: { backgroundColor: shades[50], backgroundImage: 'none' } } },
    },
  };
};

const getDarkOptions = (data: CustomThemeData | null): ThemeOptions => {
  const isCustom = !!data;
  const primary = data?.primary || '#0E70FF';
  const primaryContrast = data?.primaryContrast || '#e6e6e6';
  const secondary = data?.secondary || '#1A4488';
  const secondaryContrast = data?.secondaryContrast || '#f7f7f7';
  const info = data?.info || '#5FA2FF';
  const error = data?.error || '#A70D0D';
  const errorDark = '#870505';
  const errorContrast = data?.errorContrast || '#f7f7f7';
  const warning = data?.warning || '#DC7414';
  const brandPrimary = data?.brandPrimary || '#0E70FF';
  const brandSecondary = data?.brandSecondary || '#1A4488';
  // Custom themes darken the org's primary color for gradients; the stock
  // theme uses the brand pair.
  const primaryDark = isCustom ? data?.primaryDark || '#002e7a' : brandSecondary;
  const gradientStart = isCustom ? primary : brandPrimary;
  const shades = { ...defaultShades.dark, ...data?.shades };

  const inputStyles = {
    height: '36px',
    paddingTop: 0,
    paddingBottom: 0,
    backgroundColor: shades[400],
  };

  return {
    palette: {
      mode: 'dark',
      background: { default: shades[50] },
      text: { primary: isCustom ? primaryContrast : '#eeeeee' },
      primary: { main: primary, dark: primaryDark, contrastText: primaryContrast },
      secondary: {
        main: secondary,
        ...(isCustom ? {} : { dark: brandSecondary }),
        contrastText: secondaryContrast,
      },
      ...(isCustom
        ? { success: { main: data?.positive || '#6ab78a' }, action: { hover: shades[200] } }
        : {}),
      info: { main: info },
      warning: { main: warning },
      shade: shades,
      tooltip: { main: '#fff3cd' },
      brand: {
        main: brandPrimary,
        ...(isCustom ? { dark: brandSecondary } : {}),
        contrastText: primaryContrast,
      },
      error: { main: error, contrastText: errorContrast },
    },
    components: {
      MuiAppBar: { styleOverrides: { root: { background: shades[100] } } },
      MuiDrawer: {
        styleOverrides: { paper: { backgroundImage: shades[200], borderRight: 'none' } },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            ...inputStyles,
            border: 'none',
            '&:focus, &:hover': { border: 'none' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-notchedOutline':
              { borderColor: 'transparent' },
          },
        },
      },
      MuiInput: {
        styleOverrides: {
          root: {
            ...inputStyles,
            borderColor: 'transparent',
            '&.Mui-focused .MuiInput-notchedOutline, &:hover .MuiInput-notchedOutline': {
              borderColor: 'transparent',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            ...inputStyles,
            borderColor: 'transparent',
            '&.Mui-focused .MuiOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-notchedOutline':
              { borderColor: 'transparent' },
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          inputRoot: { '& .MuiOutlinedInput-notchedOutline': { border: 'none' } },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {},
          head: {
            backgroundColor: shades[300],
            color: grey[500],
            fontWeight: 500,
            textTransform: 'uppercase',
          },
          body: {
            backgroundColor: shades[200],
            borderBottom: '1px solid rgba(224, 224, 224, 0.2)',
          },
        },
      },
      ...(isCustom
        ? {}
        : { MuiTablePagination: { styleOverrides: { root: { backgroundColor: shades[200] } } } }),
      MuiButton: {
        variants: [
          {
            props: { variant: 'contained', color: 'primary' },
            style: {
              background: `linear-gradient(165deg, ${gradientStart}, ${primaryDark})`,
              color: 'white',
              '&:hover': {
                background: `linear-gradient(300deg, ${gradientStart}, ${primaryDark})`,
              },
              ...iconWithSpacing,
              '&.Mui-disabled': { background: shades[300] },
            },
          },
          {
            props: { variant: 'contained', color: 'secondary' },
            style: {
              background: `linear-gradient(165deg, ${shades[700]}, ${shades[600]})`,
              color: 'white',
              '&:hover': { background: `linear-gradient(300deg, ${shades[700]}, ${shades[600]})` },
              ...iconWithSpacing,
              '&.Mui-disabled': { background: grey[200], color: grey[500] },
            },
          },
          {
            props: { variant: 'contained', color: 'error' },
            style: {
              background: `linear-gradient(165deg, ${error}, ${errorDark})`,
              color: 'white',
              '&:hover': { background: `linear-gradient(300deg, ${error}, ${errorDark})` },
              ...iconWithSpacing,
              '&.Mui-disabled': { background: shades[300] },
            },
          },
          {
            props: { variant: 'outlined', color: 'secondary' },
            style: {
              border: `1px solid ${shades[900]}`,
              color: shades[900],
              '&:hover': { border: `1px solid ${info}`, color: info, opacity: 0.9 },
              ...iconWithSpacing,
              '&.Mui-disabled': { color: grey[500] },
            },
          },
        ],
      },
      MuiPaper: { styleOverrides: { root: { backgroundColor: shades[50], backgroundImage: 'none' } } },
    },
  };
};

/**
 * Single entry point for every Evodicom theme (stock and per-organization).
 *
 * Style changes that should apply app-wide belong in `appSurfaces.ts` (shared
 * surface tokens and component patterns), not in individual component `sx`.
 */
const createEvodicomTheme = (
  mode: PaletteMode,
  customData: CustomThemeData | null = null,
  scope: ThemeScope = 'app',
): Theme => {
  const modeOptions = mode === 'light' ? getLightOptions(customData) : getDarkOptions(customData);
  const options: ThemeOptions = {};
  merge(options, modeOptions, baseTheme);

  if (scope === 'viewer') return createTheme(options);

  // The surface layer reads the resolved palette (shades, primary…), so build
  // once to resolve it, then merge the layer on top.
  const resolved = createTheme(options);
  const surfaces = getAppSurfaces(resolved);
  const { MuiButton: surfaceButton, ...surfaceComponents } = surfaces.components ?? {};
  merge(options, { ...surfaces, components: surfaceComponents });
  // lodash.merge would merge `variants` arrays index by index; append them
  // instead so the surface layer's variants come last and win.
  options.components!.MuiButton!.variants = [
    ...(options.components?.MuiButton?.variants ?? []),
    ...(surfaceButton?.variants ?? []),
  ];
  return createTheme(options);
};

export default createEvodicomTheme;
